import { Layout } from "@/components/Layout";
import { FinancingCalculator } from "@/components/FinancingCalculator";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Percent, CalendarRange, Wallet, ShieldCheck, FileText, ArrowRight } from "lucide-react";

export default function Financing() {
  return (
    <Layout>
      <div className="border-b border-slate-200 bg-[#E3ECFB] py-16">
        <div className="container-x max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Flexible Financing</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-slate-900 lg:text-5xl">Financing Built Around You</h1>
          <p className="mt-4 text-lg text-slate-600">Transparent terms, flexible down payments, and a fast online application. Estimate your payment, then apply securely in minutes.</p>
        </div>
      </div>

      <div className="container-x py-16">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Wallet, "From 10% Down", "Minimum 10% down payment, with options up to 40% or a custom amount."],
            [CalendarRange, "Terms up to 48mo", "Choose 12, 18, 24, 30, 36, 42, or 48-month repayment schedules."],
            [Percent, "Competitive APR", "Clear, competitive rates applied consistently across your term."],
            [ShieldCheck, "Secure & Private", "Your documents are stored privately and never shared publicly."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="card-surface p-7 hover-lift">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl gold-gradient text-[#EEF3FC]"><Icon className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-sm text-slate-600">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
          <div>
            <h2 className="font-display text-3xl font-bold text-slate-900">How Our Financing Works</h2>
            <div className="mt-6 space-y-5">
              {[
                ["Estimate", "Use the calculator to model your monthly payment with different down payments and terms."],
                ["Apply", "Complete a secure multi-step application with your personal and employment details."],
                ["Upload Documents", "Securely upload your driver's license, proof of address, and any requested documents."],
                ["Review & Decision", "Our finance team reviews your application and you receive a decision in your dashboard."],
                ["Track Payments", "Once approved, your financing contract and payment schedule appear in your dashboard."],
              ].map(([t, d], i) => (
                <div key={t} className="flex gap-4">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full gold-gradient font-display text-sm font-bold text-[#EEF3FC]">{i + 1}</div>
                  <div><h4 className="font-semibold text-slate-900">{t}</h4><p className="text-sm text-slate-600">{d}</p></div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-2 rounded-xl border border-gold/20 bg-gold/5 p-4 text-sm text-slate-700">
              <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
              All figures shown are estimates and do not constitute a financing offer or approval. See our <Link to="/financing-disclosure" className="mx-1 text-gold underline">Financing Disclosure</Link>.
            </div>
            <Button asChild className="btn-gold mt-8 rounded-full px-8 py-6"><Link to="/inventory" data-testid="financing-browse-btn">Browse Inventory <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          </div>
          <FinancingCalculator initialPrice={42990} />
        </div>
      </div>
    </Layout>
  );
}
